import { useCallback } from 'react'

import { parseUnits } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { useActiveOrg } from '~/hooks/org'
import { TransferFormValues } from '~/hooks/smart-account-actions/types'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { BasicTransactionType, IAddress, OrgTransaction } from '~/types'
import { Address } from '~/utils/address'
import { shortenAddress } from '~/utils/addresses'
import { displayBigNumber } from '~/utils/display'
import { sumStringTokenAmounts } from '~/utils/tokens'

type CreateTransferTransactionData = Omit<TransferFormValues, 'isMax'> & {
  calls: {
    to: IAddress
    data: string
    value: bigint
  }[]
}

type CreateTransferTransactionParams = {
  transactionId: string | undefined
  data: CreateTransferTransactionData
  dontOpenDialog: boolean
  onSuccess?: (transaction: OrgTransaction) => void
}

export const useCreateTransferTransaction = ({
  token,
  chainId,
}: {
  token: IAddress
  chainId: SupportedChainId
}): {
  createTransferTransaction: (
    args: CreateTransferTransactionParams,
  ) => Promise<void>
  isProcessing: boolean
} => {
  const { org } = useActiveOrg()
  const metadata = useTokenMetadata({
    address: token,
    chainId,
    includePrice: true,
  })

  const { convertTokenToUSD } = useTokenPrices()
  const { createTransaction, isProcessing } = useCreateTransaction()

  const createTransferTransaction = useCallback(
    async ({
      transactionId,
      data,
      dontOpenDialog,
      onSuccess,
    }: CreateTransferTransactionParams) => {
      if (!metadata) {
        throw new Error('Token metadata not found')
      }

      const singleRecipient =
        data.recipients.length === 1 ? data.recipients[0] : undefined
      const bankAccountMatch = singleRecipient
        ? org?.bridge?.externalAccounts.find(({ liquidationAddress }) =>
            Address.equal(singleRecipient.address, liquidationAddress),
          )
        : undefined
      const isOfframp = bankAccountMatch !== undefined
      const totalAmount = sumStringTokenAmounts(
        data.recipients.map((recipient) => recipient.amount),
        metadata,
      )
      const amountUsd = convertTokenToUSD({
        token: data.token,
        amount: totalAmount,
        chainId,
      })

      const recipientName = bankAccountMatch
        ? bankAccountMatch.name
        : data.recipients.length > 1
          ? `${data.recipients.length} recipients`
          : shortenAddress(singleRecipient!.address)

      await createTransaction({
        transactionId,
        txData: {
          chainId,
          name: `${
            bankAccountMatch ? 'Withdraw' : 'Transfer'
          } ${displayBigNumber(totalAmount, {
            displayDecimals: 4,
            tokenDecimals: metadata.decimals,
            minDisplayDecimals: 0,
          })} ${metadata?.symbol} to ${recipientName}`,
          smartAccountId: data.smartAccountId,
          smartAccountAddress: data.smartAccountAddress,
          calls: data.calls,
          onSuccess,
          memo: data.memo,
          types: [BasicTransactionType.Transfer],
          isOfframp,
          transactionData: {
            transferData: {
              recipients: data.recipients.map((recipient) => ({
                address: recipient.address,
                amount: parseUnits(
                  recipient.amount,
                  metadata.decimals,
                ).toString(),
                compliantPaymentRelationId:
                  recipient.compliantPaymentRelationId,
              })),
              tokenAddress: token,
            },
          },
        },
        dontOpenDialog,
        loggingData: {
          chainId,
          sender: data.smartAccountAddress,
          recipients: data.recipients.map((recipient) => recipient.address),
          token: data.token,
          tokenSymbol: metadata?.symbol ?? 'unknown',
          totalAmount: totalAmount.toString(),
          amountUsd: amountUsd ?? 0,
          isOfframp,
        },
      })
    },
    [
      org?.bridge?.externalAccounts,
      metadata,
      createTransaction,
      chainId,
      token,
      convertTokenToUSD,
    ],
  )

  return {
    createTransferTransaction,
    isProcessing,
  }
}
